import { ChangeEvent, FC, useEffect, useRef, useState } from "react";

import ImageCropDialog from "@src/lib/ui/ImageCropDialog";
import { Button, ButtonProps } from "@src/lib/ui/Button";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { ACCEPTED_IMAGE_TYPES } from "@src/lib/utils/files";

type Props = {
  onNewImageUrl: (url: string) => void;
  text: string;
  variant?: ButtonProps["variant"];
  /** Told when an upload starts and ends (a form can hold Save meanwhile). */
  onUploadingChange?: (uploading: boolean) => void;
};

const SquareImageUploadButton: FC<Props> = ({
  onNewImageUrl,
  text,
  variant,
  onUploadingChange,
}) => {
  const inputEl = useRef<HTMLInputElement>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  // From the cropped file to the URL, validation and the presign included
  // (useS3Upload's isUploading only covers the transfer).
  const [busy, setBusy] = useState(false);
  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadComplete: (upload) => {
      setBusy(false);
      onNewImageUrl(upload.url);
    },
    onUploadFailed: () => setBusy(false),
  });

  useEffect(() => {
    onUploadingChange?.(busy || isUploading);
  }, [busy, isUploading, onUploadingChange]);

  const hideCropModal = () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropImageUrl(null);
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) {
      setCropImageUrl(URL.createObjectURL(file));
    }
  };

  return (
    <>
      <Button
        onClick={() => inputEl.current?.click()}
        loading={busy || isUploading}
        variant={variant}
      >
        {text}
      </Button>
      <input
        className="hidden"
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.toString()}
        onChange={onFileChange}
        ref={inputEl}
      />
      {cropImageUrl && (
        <ImageCropDialog
          aspectRatio={1}
          lockAspectRatio
          imageUrl={cropImageUrl}
          visible={Boolean(cropImageUrl)}
          hide={hideCropModal}
          onNewImageFile={(file) => {
            hideCropModal();
            setBusy(true);
            handleFileUpload(file);
          }}
        />
      )}
    </>
  );
};

export default SquareImageUploadButton;
